import React, { useState } from 'react';
import { XMarkIcon, CubeIcon, PlusIcon } from '@heroicons/react/24/outline';
import { RackLayoutData } from './tipos';

interface ModalCrearRackProps {
  isOpen: boolean;
  onClose: () => void;
  zonasDisponibles: string[];
  pasillosDisponibles: string[];
  onCrear: (nuevoRack: Omit<RackLayoutData, 'id'>) => void;
}

export const ModalCrearRack: React.FC<ModalCrearRackProps> = ({
  isOpen,
  onClose,
  zonasDisponibles,
  pasillosDisponibles,
  onCrear,
}) => {
  const [zona, setZona] = useState<string>(zonasDisponibles[0] || 'A');
  const [pasillo, setPasillo] = useState<string>(pasillosDisponibles[0] || 'Pasillo 01');
  const [codigoRack, setCodigoRack] = useState<string>('Rack 01');
  const [niveles, setNiveles] = useState<number>(3);
  const [ancho, setAncho] = useState<number>(2.8);
  const [maxTon, setMaxTon] = useState<number>(3.5);
  const [maxUnidades, setMaxUnidades] = useState<number>(48);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onCrear({
      zona: zona.trim().toUpperCase(),
      pasillo: pasillo.trim(),
      rack: codigoRack.trim(),
      x: 0,
      z: 0,
      rotacion: 0,
      niveles,
      ancho,
      max_ton: Number(maxTon) || 3.5,
      max_unidades: Number(maxUnidades) || 48,
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/70 backdrop-blur-md p-4 animate-fadeIn">
      <div className="bg-slate-900 border border-slate-700/80 rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden flex flex-col text-white">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800 bg-slate-950/60">
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-xl bg-blue-500/20 text-blue-400 border border-blue-500/30">
              <CubeIcon className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-extrabold text-base tracking-tight">Construir Nuevo Rack Industrial</h3>
              <p className="text-xs text-slate-400">Modo Arquitecto 3D · Ingeniería Estructural</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-all"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Formulario */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1.5">
                Zona Operativa
              </label>
              <input
                type="text"
                value={zona}
                onChange={(e) => setZona(e.target.value)}
                placeholder="Ej. A, B, RECEPCION"
                className="w-full bg-slate-800/80 border border-slate-700 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-blue-500 font-bold"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1.5">
                Pasillo
              </label>
              <input
                type="text"
                value={pasillo}
                onChange={(e) => setPasillo(e.target.value)}
                placeholder="Ej. Pasillo 01"
                className="w-full bg-slate-800/80 border border-slate-700 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1.5">
              Código / Identificador del Rack
            </label>
            <input
              type="text"
              value={codigoRack}
              onChange={(e) => setCodigoRack(e.target.value)}
              placeholder="Ej. Rack 05 o R-12"
              className="w-full bg-slate-800/80 border border-slate-700 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-blue-500 font-bold"
              required
            />
          </div>

          {/* Dimensiones Estructurales */}
          <div className="p-4 bg-slate-950/60 rounded-xl border border-slate-800/80 space-y-3">
            <span className="text-[11px] font-extrabold uppercase tracking-wider text-blue-400 block">
              Dimensiones y Configuración Estructural
            </span>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-300 mb-1">
                  Niveles de Altura: <strong className="text-white">{niveles} niveles</strong>
                </label>
                <div className="flex items-center gap-2">
                  {[2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setNiveles(n)}
                      className={`flex-1 py-1.5 rounded-lg text-xs font-bold transition-all ${
                        niveles === n
                          ? 'bg-blue-600 text-white shadow-md'
                          : 'bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700'
                      }`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-300 mb-1">
                  Capacidad de Pallets por Nivel
                </label>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setAncho(2.8)}
                    className={`flex-1 py-1.5 rounded-lg text-xs font-bold transition-all ${
                      ancho === 2.8
                        ? 'bg-blue-600 text-white shadow-md'
                        : 'bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700'
                    }`}
                  >
                    2 Pallets (2.8m)
                  </button>
                  <button
                    type="button"
                    onClick={() => setAncho(4.0)}
                    className={`flex-1 py-1.5 rounded-lg text-xs font-bold transition-all ${
                      ancho === 4.0
                        ? 'bg-blue-600 text-white shadow-md'
                        : 'bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700'
                    }`}
                  >
                    3 Pallets (4.0m)
                  </button>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 pt-1">
              <div>
                <label className="block text-xs font-medium text-slate-300 mb-1">
                  Carga Máxima (Toneladas)
                </label>
                <div className="relative">
                  <input
                    type="number"
                    step="0.5"
                    min="1"
                    max="15"
                    value={maxTon}
                    onChange={(e) => setMaxTon(Number(e.target.value))}
                    className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-blue-500"
                  />
                  <span className="absolute right-3 top-2 text-xs text-slate-400 font-bold">t</span>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-300 mb-1">
                  Capacidad Máx. Unidades
                </label>
                <input
                  type="number"
                  min="1"
                  max="1000"
                  value={maxUnidades}
                  onChange={(e) => setMaxUnidades(Number(e.target.value))}
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Footer botones */}
          <div className="flex items-center justify-end gap-3 pt-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl text-xs font-bold text-slate-400 hover:text-white hover:bg-slate-800 transition-all"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold shadow-lg shadow-blue-600/30 transition-all"
            >
              <PlusIcon className="w-4 h-4" />
              Construir e Instalar en 3D
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
